import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import { fetchReceivableInvoice } from './fetch-receivable-invoice';

vi.mock('receivable-invoices-js/react/utils/api-clients/qonto');
vi.mock('receivable-invoices-js/constants/hosts', () => ({
  receivableInvoicesApiNamespace: '/api/v5',
}));

vi.mock('@repo/design-system-kit', () => ({
  useToast: vi.fn(() => ({
    showToast: vi.fn(),
  })),
}));

describe('unit | API | queries | fetch-receivable-invoice', () => {
  const mockQontoClient = vi.mocked(qontoClient);
  const invoiceId = 'invoice-123';
  const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/invoices/shared`;

  beforeEach(() => {
    vi.clearAllMocks();
  });

  describe(fetchReceivableInvoice, () => {
    it('fetches invoice successfully', async () => {
      const apiResponse = {
        data: {
          id: invoiceId,
          type: 'receivable_invoice',
          attributes: {
            amount_due: {
              value: '100.00',
              currency: 'EUR',
            },
            contact_email: 'test@example.com',
            currency: 'EUR',
            customer_snapshot: {
              kind: 'company',
              name: 'Test Company',
              id: 'customer-123',
            },
            direct_debit_enabled: false,
            due_date: '2025-12-31',
            footer: 'Footer text',
            header: 'Header text',
            is_einvoice: false,
            is_pdf_generated: true,
            issue_date: '2025-01-01',
            locale: 'en',
            logo_url: 'https://example.com/logo.png',
            number: 'INV-001',
            organization: {
              id: 'org-123',
              legal_name: 'Test Organization',
            },
            overdue: false,
            payment_link: {
              id: 'payment-link-123',
              status: 'open',
              methods: ['credit_card', 'bank_transfer'],
            },
            payment_methods: [
              {
                beneficiary_name: 'Test Org',
                bic: 'TESTBIC',
                iban: 'FR1234567890',
                type: 'transfer',
              },
            ],
            pdf_url: 'https://example.com/invoice.pdf',
            performance_start_date: '2025-01-01',
            performance_end_date: '2025-12-31',
            reference: 'REF-001',
            sections: [],
            status: 'unpaid',
            stamp_duty_amount: {
              value: '0.00',
              currency: 'EUR',
            },
            subtotal: {
              value: '90.00',
              currency: 'EUR',
            },
            terms_and_conditions: 'Terms text',
            vat_amount: {
              value: '10.00',
              currency: 'EUR',
            },
          },
          relationships: {
            receivable_invoicing_upload: {
              data: {
                id: 'upload-123',
                type: 'receivable_invoices_uploads',
              },
            },
            terms_conditions_upload: {
              data: {
                id: 'upload-456',
                type: 'receivable_invoices_uploads',
              },
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchReceivableInvoice({ id: invoiceId }, {});

      expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/${invoiceId}`);

      const expected = camelizeKeys({
        ...apiResponse.data.attributes,
        id: invoiceId,
        receivableInvoicingUploadId: 'upload-123',
        termsConditionsUploadId: 'upload-456',
      });

      expect(result).toStrictEqual(expected);
    });

    it('extracts relationship IDs correctly', async () => {
      const apiResponse = {
        data: {
          id: invoiceId,
          type: 'receivable_invoice',
          attributes: {
            amount_due: { value: '100.00', currency: 'EUR' },
            currency: 'EUR',
            number: 'INV-001',
            status: 'unpaid',
          },
          relationships: {
            receivable_invoicing_upload: {
              data: {
                id: 'upload-123',
                type: 'receivable_invoices_uploads',
              },
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchReceivableInvoice({ id: invoiceId }, {});

      expect(result).toHaveProperty('receivableInvoicingUploadId', 'upload-123');
    });

    it('handles invoice without relationships', async () => {
      const apiResponse = {
        data: {
          id: invoiceId,
          type: 'receivable_invoice',
          attributes: {
            amount_due: { value: '100.00', currency: 'EUR' },
            currency: 'EUR',
            number: 'INV-001',
            status: 'unpaid',
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchReceivableInvoice({ id: invoiceId }, {});

      expect(result).toHaveProperty('id', invoiceId);
      expect(result).toHaveProperty('amountDue');
      expect(result).toHaveProperty('receivableInvoicingUploadId', undefined);
      expect(result).toHaveProperty('termsConditionsUploadId', undefined);
    });

    it('handles API errors with custom error handler', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      const onError = vi.fn<(err: unknown) => void>();
      await fetchReceivableInvoice({ id: invoiceId }, { onError });

      expect(onError).toHaveBeenCalledWith(error);
    });

    it('throws error when no error handler is provided', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      await expect(fetchReceivableInvoice({ id: invoiceId }, {})).rejects.toThrow('API Error');
    });
  });
});
